import { useEffect, useState } from 'react'
import {
  getPurchasedSubscriptions,
  listOfferedSubscriptionPlans
} from 'services/Api/SubscriptionApi'
import { SUBSCRIPTION_UPDATED_EVENT } from 'pages/NewDashboard/components/MyPlan/usePlanCheckout'
import { isDev } from 'infra/environment'
import { BENEFIT_TXN_SMS, isWhatsappAlertBenefit } from 'utils/alertBenefits'

// Dev-only QA switch: `localStorage.okc_dev_force_no_alert_benefit = '1'` makes a premium
// account behave as if it had no alert benefit, so the upgrade flow can be exercised.
const DEV_FORCE_NO_BENEFIT_KEY = 'okc_dev_force_no_alert_benefit'
const devForcedNoBenefit = () => {
  try {
    return isDev && window.localStorage.getItem(DEV_FORCE_NO_BENEFIT_KEY) === '1'
  } catch (e) {
    return false
  }
}

// Mirrors KMP AccountProfileContract.State: which alert plan the merchant is on, and whether a
// WhatsApp-alert plan is on offer (drives the SMS-or-WhatsApp sheet and the upsell copy).
export interface TxnAlertBenefit {
  // Plan and offer loaded — until then switching ON is ignored, so a premium merchant never
  // sees the Buy Plan sheet because of a slow response.
  loaded: boolean
  // OkCredit sends the alerts under the active plan (SMS or WhatsApp)
  hasAlertBenefit: boolean
  // SMS-from-OkCredit plan without the WhatsApp benefit
  isOnSmsPremium: boolean
  isOnWhatsappPlan: boolean
  // A WhatsApp-alert plan is on offer
  whatsappOffered: boolean
}

const NOT_LOADED: TxnAlertBenefit = {
  loaded: false,
  hasAlertBenefit: false,
  isOnSmsPremium: false,
  isOnWhatsappPlan: false,
  whatsappOffered: false
}

// Fetched on every mount (like KMP loading the screen): the plan can change outside this
// tab (a purchase on the phone), so nothing is cached across the session.
const load = (): Promise<TxnAlertBenefit> =>
  Promise.all([getPurchasedSubscriptions(), listOfferedSubscriptionPlans()])
    .then(([purchased, offered]) => {
      const benefits: string[] = purchased.data?.active_sub?.benefits || []
      const plans = offered.data?.subscription_plans || []
      const isOnWhatsappPlan = benefits.some(isWhatsappAlertBenefit)
      const isOnSmsPremium = benefits.includes(BENEFIT_TXN_SMS) && !isOnWhatsappPlan
      const forcedOff = devForcedNoBenefit()
      return {
        loaded: true,
        hasAlertBenefit: !forcedOff && (isOnWhatsappPlan || isOnSmsPremium),
        isOnSmsPremium: !forcedOff && isOnSmsPremium,
        isOnWhatsappPlan: !forcedOff && isOnWhatsappPlan,
        whatsappOffered: plans.some((plan) =>
          (plan.benefits as string[]).some(isWhatsappAlertBenefit)
        )
      }
    })
    .catch(() => ({
      // Fail open: a network error must not lock a paying merchant out of the toggle.
      loaded: true,
      hasAlertBenefit: true,
      isOnSmsPremium: false,
      isOnWhatsappPlan: false,
      whatsappOffered: false
    }))

export const useTxnAlertBenefit = (): TxnAlertBenefit => {
  const [state, setState] = useState<TxnAlertBenefit>(NOT_LOADED)

  useEffect(() => {
    let active = true
    const run = () => {
      load().then((result) => {
        if (active) {
          setState(result)
        }
      })
    }
    run()
    // A purchase in this tab (My Plan, expiry banner) changes the answer at once.
    window.addEventListener(SUBSCRIPTION_UPDATED_EVENT, run)
    return () => {
      active = false
      window.removeEventListener(SUBSCRIPTION_UPDATED_EVENT, run)
    }
  }, [])

  return state
}
